import type { ReactNode } from 'react'
import React, { useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import type { SelectMultipleProps as BaseSelectMultipleProps } from '@ur-solutions/react-components'
import type {
  AllDepartmentsQuery,
  AllDepartmentsQueryVariables,
  DepartmentNode,
} from '@/modules/companies'
import { ALL_DEPARTMENTS_QUERY } from '@/modules/companies'
import { useQuery } from '@apollo/client'
import { useDebounce } from '@/utils/hooks'
import type { SelectMultipleOnSelectAll } from './SelectMultiple'
import { SelectMultiple } from './SelectMultiple'
import { useTranslate } from '@ur-solutions/react-hooks'
import { ZIndexRange } from '@/types/style'
import { DropdownNames } from './types'

const Department = styled.div`
  display: flex;
  align-items: center;

  span.name {
    margin-left: 0.6rem;
  }
`

interface SelectMulitpleRelativeWrapperProps {
  width: string
}

const SelectMultipleRelativeWrapper = styled.div<SelectMulitpleRelativeWrapperProps>`
  position: relative;
  width: ${props => props.width};
`

interface SelectMulitpleWrapperProps {
  open: boolean
  numSelectedText: string
  compact: boolean
  width: string
}

const SelectMulitpleWrapper = styled.div<SelectMulitpleWrapperProps>`
  width: ${props => props.width};
  ${props =>
    !props.open
      ? css`
          ${props.compact &&
          `
          .--select-display-inner > :not(:first-child), .--select-display-selected > i, .--select-display-selected span {
            display: none;
          }
          .--select-display-selected :after {
            content: '${props.numSelectedText}';
          }
        `}
        `
      : `
      position: absolute;
      z-index: ${ZIndexRange.Dropdowns};
    `}
`

export interface SelectMultipleProps
  extends BaseSelectMultipleProps<string, any> {
  compact?: boolean
  grayed?: boolean
  dropdownPageName?: DropdownNames
  withSelectAll?: boolean
  dataset?: DepartmentNode[]
  onSelectAll?: SelectMultipleOnSelectAll
}
export interface SelectMultipleOptions {
  value: string
  label: ReactNode
}
export const SelectMultipleDepartments: React.FC<
  Omit<SelectMultipleProps, 'options'>
> = ({
  selected,
  // TODO unused?
  dataset: _dataset = [],
  compact = true,
  dropdownPageName = DropdownNames.TIME_ENTRIES,
  width = '300px',
  grayed = true,
  onChange,
  ...selectMultipleProps
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query)

  const translations = useTranslate({
    nDepartments: ['settings.n-departments', { n: 0 }],
  })

  const { data } = useQuery<AllDepartmentsQuery, AllDepartmentsQueryVariables>(
    ALL_DEPARTMENTS_QUERY,
    {
      variables: {
        q: debouncedQuery,
        orderBy: 'lastName',
        dropdown: dropdownPageName,
      },
      fetchPolicy: 'cache-and-network',
    }
  )

  const getLabel = useCallback((department: DepartmentNode) => {
    return (
      <Department>
        <span className="name">{department.name}</span>
      </Department>
    )
  }, [])

  const options =
    data?.allDepartments.edges.map(({ node: department }) => ({
      value: department.id,
      label: getLabel(department),
    })) ?? []

  return (
    <SelectMultipleRelativeWrapper width={width}>
      <SelectMulitpleWrapper
        open={open}
        numSelectedText={translations.nDepartments({ n: selected.length })}
        compact={compact}
        width={width}
      >
        <SelectMultiple
          selected={selected}
          options={options}
          searchable={'custom'}
          fullWidth
          grayed={grayed}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onFilterChange={setQuery}
          onChange={onChange}
          {...selectMultipleProps}
        />
      </SelectMulitpleWrapper>
    </SelectMultipleRelativeWrapper>
  )
}
